import React, { useState, useMemo, useEffect } from 'react';
import { Box, Flex, TextInput, Card, Checkbox, Button, SelectDropdown } from '@kistech/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as adminsSelectors, actions as adminsActions, CreateAdminPayload } from 'store/admins';
import { useSelector } from 'react-redux';
import {
  getInitialForm,
  validationSchema,
  getHasEmptyInput,
  getHasInvalidInput,
  defaultRoles,
  globalRole
} from '../utils';
import { getSortedList } from 'utils/stringFormat';
import { MAINTENANCE, INVALID, VALID } from '../constants';
import { ValidPasswordToolTip } from '../../ValidPasswordToolTip';
import { selectors as venuesSelectors, actions as venuesActions, Venue } from 'store/venues';
import { Grid, Typography as MuiTypography, Box as MuiBox } from '@mui/material';
import { selectors as authSelectors } from 'store/auth';
import { useStyles } from '../styled';
import { RoleItem } from '../types';

export const CreateNewUserForm = () => {
  // ACTION CREATORS AND SELECTORS
  const [createAdmin, getVenues, getValidPinCode, resetError, resetPinCode] = useActions([
    adminsActions.createAdmin,
    venuesActions.getVenues,
    adminsActions.getValidPinCode,
    adminsActions.resetError,
    adminsActions.resetPinCode
  ]);
  const { pending, error } = useSelector(adminsSelectors.selectAction);
  const venues = useSelector(venuesSelectors.selectVenues);
  const admin = useSelector(authSelectors.selectAdmin);
  const validPincode = useSelector(adminsSelectors.selectPinCode);
  const classes = useStyles();

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getInitialForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});
  const [showPasswordInfo, setPasswordInfoVisibility] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue>(venues[0]);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);

  // GET venue list if its not available
  useEffect(() => {
    if (!venues || !venues.length) getVenues();
    if (!validPincode) getValidPinCode();

    return () => resetError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (venues && venues.length) setSelectedVenue(venues[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues]);

  // ON UPDATE OF FORM.ROLE
  useEffect(() => {
    // SET THE FORM TO BE THE PREVIOUS FROM WITH PASSWORD KEY ADDED
    // Resetting password to empty if role change to Maintenance

    if (form.role === MAINTENANCE) {
      setForm((f) => ({ ...f, password: '' }));
    }
  }, [form.role]);

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form), [form]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  React.useEffect(() => {
    let tempRoles: RoleItem[] = [];
    if (admin && admin.role === 'GlobalAdmin') {
      tempRoles = globalRole;
    }
    tempRoles = tempRoles.concat(defaultRoles);
    setRoles(tempRoles);
  }, [admin]);

  React.useEffect(() => {
    if (validPincode) {
      setForm({ ...form, pinCode: validPincode });
    }

    return () => resetPinCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validPincode]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // FORM INPUT CHANGE
  const onInputChange = (key: keyof ReturnType<typeof getInitialForm>, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });

    if (key === 'password') {
      setPasswordInfoVisibility(true);
    }
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await validationSchema.validate({ [key]: value && value.toString().trim() }).catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
    if (key === 'password') {
      setPasswordInfoVisibility(false);
    }
  };

  // SEND ALERTS CHECKBOX CLICK
  const onAlertCheckboxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // SETS FORM MAINTENANCE OR CLIENT ADMIN CHECKBOXES
    setForm({ ...form, sendAlertEmails: !form.sendAlertEmails });
  };

  const handleRoleChange = (role: RoleItem) => {
    setSelectedRole(role);
    setForm({ ...form, role: role.code });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getInitialForm>) => {
    return validationMessage[key];
  };

  // FORM SUBMIT
  const onCreateUserClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // SUBMITS THE FORM TO CREATE ADMIN
    const payload: CreateAdminPayload = {
      name: form.name,
      email: form.email,
      password: form.password,
      role: form.role,
      sendAlertEmails: form.sendAlertEmails,
      venueId: form.role === MAINTENANCE && selectedVenue ? selectedVenue.id : undefined,
      pinCode: form.pinCode
    };
    createAdmin(payload);
  };

  // CHECKS ROLE
  const isMaintenance = form.role === MAINTENANCE;

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  return (
    <Flex justify="center" flexOverride="1 1 0%" padding="0 0 11">
      <Flex direction="column" fullWidth align="center" justify="center">
        <Grid className={classes.createUserInnerContainer}>
          <Box flexOverride="none" fullWidth margin="0 0 5">
            <MuiTypography className={classes.createNewUserHeading}>Create New User</MuiTypography>
          </Box>

          <MuiBox className={classes.createVenueBox}>
            <MuiTypography className={classes.createVenueLabel}>Role</MuiTypography>
            <SelectDropdown
              placeholder="Select a Role"
              items={getSortedList(roles, 'name')}
              selectedItem={selectedRole as RoleItem}
              onChange={(item: RoleItem) => handleRoleChange(item)}
              itemToString={(item: RoleItem) => (item ? item.name : '')}
            />
          </MuiBox>
          {/* INPUT - NAME */}
          <MuiBox className={classes.createUserInputBox}>
            <TextInput
              label="Name"
              size="medium"
              fullWidth
              placeholder="New User's Name"
              value={form.name}
              onChange={(e) => onInputChange('name', e)}
              onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
              validate={validate('name')}
              isError={validation.name === INVALID}
            />
          </MuiBox>
          {/* INPUT - EMAIL */}
          <MuiBox className={classes.createUserInputBox}>
            <TextInput
              label="Email Address"
              size="medium"
              fullWidth
              placeholder="New User's Email Address"
              value={form.email}
              onChange={(e) => onInputChange('email', e)}
              onBlur={(e) => onInputBlur('email', e, e.target.value.trim())}
              validate={validate('email')}
              isError={validation.email === INVALID}
            />
          </MuiBox>
          {!isMaintenance && (
            <MuiBox className={classes.createUserInputBox}>
              <TextInput
                label="Admin Password"
                size="medium"
                fullWidth
                placeholder="Admin Password"
                value={form.password}
                type="password"
                onChange={(e) => onInputChange('password', e)}
                onBlur={(e) => onInputBlur('password', e, e.target.value.trim())}
                onFocus={() => setPasswordInfoVisibility(true)}
                validate={validate('password')}
                isError={validation.password === INVALID}
                endAdornment={
                  <ValidPasswordToolTip showValidPasswordInfo={showPasswordInfo} value={form.password || ''} />
                }
              />
            </MuiBox>
          )}
          <Grid className={classes.createNewUserPinBlock}>
            {/* INPUT - Pin code */}
            <MuiBox className={classes.userFormTextInputBox}>
              <TextInput
                label="PIN Code"
                size="medium"
                placeholder="Enter 5 Digit PIN Code"
                value={form.pinCode}
                onChange={(e) => onInputChange('pinCode', e)}
                onBlur={(e) => onInputBlur('pinCode', e, e.target.value.trim())}
                validate={validate('pinCode')}
                isError={validation.pinCode === INVALID}
              />
            </MuiBox>
            {/* INPUT - Button */}
            <MuiBox className={classes.userFormButtonBox}>
              <Button
                size="small"
                onClick={getValidPinCode}
                isLoading={pending}
                width={220}
                type="submit"
                className={classes.createPinCode}
              >
                Generate New PIN
              </Button>
            </MuiBox>
          </Grid>
          {isMaintenance && (
            <MuiBox className={classes.createVenueBox}>
              <MuiTypography className={classes.createVenueLabel}>Venue Name</MuiTypography>
              <SelectDropdown
                placeholder="Select a Venue"
                items={getSortedList(venues, 'name')}
                selectedItem={selectedVenue}
                onChange={(item: Venue) => setSelectedVenue(item)}
                itemToString={(item: Venue) => (item ? item.name : '')}
              />
            </MuiBox>
          )}

          <Box flexOverride="none" fullWidth margin="0 1 4">
            <Card>
              <Card.ActionsArea onClick={onAlertCheckboxClick}>
                <Grid className={classes.createSendAlertBlock}>
                  <Checkbox size="medium" label="Send alert emails" checked={form.sendAlertEmails} />
                </Grid>
              </Card.ActionsArea>
            </Card>
          </Box>
        </Grid>

        {error && <MuiTypography className={classes.createUserErrorText}>{error}</MuiTypography>}

        {/* FORM SUBMIT */}
        <MuiBox className={classes.createUserButtonBox}>
          <Button
            isDisabled={buttonDisabled}
            size="medium"
            onClick={onCreateUserClick}
            isLoading={pending}
            width={250}
            type="submit"
            className={classes.createUserButton}
          >
            Create User
          </Button>
        </MuiBox>
      </Flex>
    </Flex>
  );
};
